import React, { useState, useEffect } from 'react';
import {
  Table, Button, Tag, Input, Modal, message, Typography, Empty, Result,
  Descriptions, Space, Tooltip, Card, Row, Col, Select, Spin, Divider, Segmented, Badge,
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, FileTextOutlined,
  SaveOutlined, LinkOutlined, UserAddOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  getFarmaceuticos,
  updateFarmaceuticoEstado,
  getClienteGescom,
  vincularFarmaceutico,
  getCandidatosGescom,
  crearFarmaceuticoGescom,
  completarFarmaceuticoGescom,
  ESTADO_PENDIENTE,
  ESTADO_REALIZADO,
  ESTADO_DESCARTADO,
  type ReconciliacionFarmaceutico,
  type ClienteGescomData,
  type CandidatoGescom,
  type ResultadoVinculacion,
  type CrearFarmaceuticoGescomRequest,
  type ResultadoCreacionFarmaceutico,
} from '../../api/reconciliacionApi';
import { fct } from '../../fct/tokens/tokens';
import { FctAviso, FctEstado, FctGlifoAtencion, claseFilaAtencion } from '../../fct/react';
import { EstadoOperacion } from './semantica';

const { Text } = Typography;
const { TextArea } = Input;


const estadoLabels: Record<number, { label: string }> = {
  0: { label: 'Pendiente' },
  1: { label: 'Realizado' },
  2: { label: 'Descartado' },
};

interface FarmaceuticosTabProps {
  selectedRecIds?: number[];
}

const FarmaceuticosTab: React.FC<FarmaceuticosTabProps> = ({ selectedRecIds }) => {
  const ESTADO_TODOS = -1;
  const [loading, setLoading] = useState(true);
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState<ReconciliacionFarmaceutico[]>([]);
  const [filteredData, setFilteredData] = useState<ReconciliacionFarmaceutico[]>([]);
  const [selectedEstado, setSelectedEstado] = useState<number>(ESTADO_PENDIENTE);
  const [estadoCounts, setEstadoCounts] = useState({ pendiente: 0, realizado: 0, descartado: 0 });
  const [searchText, setSearchText] = useState('');

  // Modal detalle
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ReconciliacionFarmaceutico | null>(null);
  const [editNotas, setEditNotas] = useState('');
  const [editEstadoId, setEditEstadoId] = useState<number>(ESTADO_PENDIENTE);
  const [actionLoading, setActionLoading] = useState(false);

  // Cómo quedó la última vinculación de este detalle (#163)
  const [resultadoVinculacion, setResultadoVinculacion] = useState<ResultadoVinculacion | null>(null);

  // Ficha del cliente de Gescom
  const [clienteGescomIdInput, setClienteGescomIdInput] = useState('');
  const [clienteGescomData, setClienteGescomData] = useState<ClienteGescomData | null>(null);
  const [clienteGescomLoading, setClienteGescomLoading] = useState(false);
  const [clienteGescomError, setClienteGescomError] = useState<string | null>(null);

  // Vinculación
  const [vinculando, setVinculando] = useState(false);

  // Buscar en Gescom (GDO-T38)
  const [buscarVisible, setBuscarVisible] = useState(false);
  const [buscarTexto, setBuscarTexto] = useState('');
  const [candidatos, setCandidatos] = useState<CandidatoGescom[]>([]);
  const [buscandoCandidatos, setBuscandoCandidatos] = useState(false);
  const [busquedaHecha, setBusquedaHecha] = useState(false);

  // Crear en Gescom y vincular en un paso (#164)
  const [crearVisible, setCrearVisible] = useState(false);
  const [crearDatos, setCrearDatos] = useState<CrearFarmaceuticoGescomRequest>({ tratamientoId: 0, nombre: '', apellido1: '' });
  const [creando, setCreando] = useState(false);
  const [crearResultado, setCrearResultado] = useState<ResultadoCreacionFarmaceutico | null>(null);

  const fetchByEstado = async (estadoId: number) => {
    const apiEstado = estadoId === ESTADO_TODOS ? undefined : estadoId;
    if (!selectedRecIds || selectedRecIds.length === 0) return getFarmaceuticos(undefined, apiEstado);
    const results = await Promise.all(selectedRecIds.map(rid => getFarmaceuticos(rid, apiEstado)));
    return results.flat();
  };

  const loadCounts = async () => {
    setLoadingCounts(true);
    try {
      const [pendientes, realizados, descartados] = await Promise.all([
        fetchByEstado(ESTADO_PENDIENTE),
        fetchByEstado(ESTADO_REALIZADO),
        fetchByEstado(ESTADO_DESCARTADO),
      ]);
      setEstadoCounts({ pendiente: pendientes.length, realizado: realizados.length, descartado: descartados.length });
    } catch { /* counts fail silently */ }
    finally { setLoadingCounts(false); }
  };

  const loadData = async () => {
    setLoading(true);
    setError(false);
    try {
      const result = await fetchByEstado(selectedEstado);
      setData(result);
      applySearch(result, searchText);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCounts();
  }, [selectedRecIds]);

  useEffect(() => {
    loadData();
  }, [selectedEstado, selectedRecIds]);

  const applySearch = (items: ReconciliacionFarmaceutico[], text: string) => {
    if (!text.trim()) {
      setFilteredData(items);
      return;
    }
    const lower = text.toLowerCase();
    setFilteredData(items.filter(f =>
      (f.nombre || '').toLowerCase().includes(lower) ||
      (f.numColegiado || '').toLowerCase().includes(lower) ||
      (f.nif || '').toLowerCase().includes(lower) ||
      (f.colegio || '').toLowerCase().includes(lower)
    ));
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    applySearch(data, value);
  };

  const handleUpdateEstado = async (id: number, estadoId: number, notasGestion?: string) => {
    setActionLoading(true);
    try {
      await updateFarmaceuticoEstado(id, estadoId, notasGestion);
      const label = estadoLabels[estadoId]?.label || 'actualizado';
      message.success(`Farmacéutico ${label.toLowerCase()} correctamente`);
      setModalVisible(false);
      loadData();
      loadCounts();
    } catch {
      message.error('Error al actualizar el farmacéutico');
    } finally {
      setActionLoading(false);
    }
  };

  const openDetail = async (item: ReconciliacionFarmaceutico) => {
    setSelectedItem(item);
    setEditNotas(item.notasGestion || '');
    setEditEstadoId(item.estadoId);
    setClienteGescomIdInput('');
    setClienteGescomData(null);
    setClienteGescomError(null);
    setResultadoVinculacion(null);
    setCrearResultado(null);
    setModalVisible(true);

    // Si ya está vinculado, cargar automáticamente los datos de Gescom
    if (item.idColegiadoGS) {
      setClienteGescomLoading(true);
      try {
        const data = await getClienteGescom(item.idColegiadoGS);
        setClienteGescomData(data);
      } catch {
        // No bloquear la apertura si falla la carga
      } finally {
        setClienteGescomLoading(false);
      }
    }
  };

  // Buscar el cliente de Gescom por ID
  const handleBuscarClienteGescom = async () => {
    const id = parseInt(clienteGescomIdInput, 10);
    if (isNaN(id) || id <= 0) {
      setClienteGescomError('Introduce un ID numérico válido');
      return;
    }
    setClienteGescomLoading(true);
    setClienteGescomError(null);
    setClienteGescomData(null);
    try {
      const data = await getClienteGescom(id);
      setClienteGescomData(data);
    } catch {
      setClienteGescomError('No se encontró el cliente en Gescom o error de conexión');
    } finally {
      setClienteGescomLoading(false);
    }
  };

  // Vincular registros (#163): en Gescom y, después, en CU2. La respuesta dice cómo quedó; un rechazo llega como 409.
  const handleVincular = async () => {
    if (!selectedItem || !clienteGescomData) return;
    setVinculando(true);
    setResultadoVinculacion(null);
    try {
      const result = await vincularFarmaceutico(selectedItem.id, clienteGescomData.id);
      setResultadoVinculacion(result);

      // Recargar la ficha de Gescom, que ya apunta al colegiado.
      try {
        setClienteGescomData(await getClienteGescom(clienteGescomData.id));
      } catch {
        // Si falla la recarga, no bloqueamos
      }

      // En Gescom queda vinculado en los tres casos: el item local lo refleja sin cerrar el modal.
      const updated = { ...selectedItem, idColegiadoGS: clienteGescomData.id };
      setSelectedItem(updated);
      setData(prev => prev.map(f => f.id === updated.id ? updated : f));
      setFilteredData(prev => prev.map(f => f.id === updated.id ? updated : f));
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Error al vincular los registros';
      message.error(msg);
    } finally {
      setVinculando(false);
    }
  };

  // Cargar el card "Datos Gescom" con un ID conocido (candidato seleccionado o registro recién creado)
  const loadGescomCliente = async (id: number) => {
    setClienteGescomIdInput(String(id));
    setClienteGescomLoading(true);
    setClienteGescomError(null);
    setClienteGescomData(null);
    try {
      const data = await getClienteGescom(id);
      setClienteGescomData(data);
    } catch {
      setClienteGescomError('No se encontró el cliente en Gescom o error de conexión');
    } finally {
      setClienteGescomLoading(false);
    }
  };

  // --- Buscar en Gescom (GDO-T38) ---

  const openBuscar = () => {
    setBuscarTexto(selectedItem?.nombre || '');
    setCandidatos([]);
    setBusquedaHecha(false);
    setBuscarVisible(true);
  };

  const handleBuscarCandidatos = async () => {
    if (buscarTexto.trim().length < 3) {
      message.warning('Escribe al menos 3 caracteres para buscar');
      return;
    }
    setBuscandoCandidatos(true);
    try {
      const result = await getCandidatosGescom(buscarTexto.trim());
      setCandidatos(result);
      setBusquedaHecha(true);
    } catch {
      message.error('Error al buscar candidatos en Gescom');
    } finally {
      setBuscandoCandidatos(false);
    }
  };

  const handleSeleccionCandidato = (c: CandidatoGescom) => {
    setBuscarVisible(false);
    loadGescomCliente(c.id);
  };

  // --- Crear en Gescom (GDO-T38) ---

  // "Apellidos, Nombre" CU2 → campos separados (heurística: apellido1 = primer token)
  const splitNombreCU2 = (full?: string) => {
    const s = (full || '').trim();
    const coma = s.indexOf(',');
    const apellidos = (coma >= 0 ? s.slice(0, coma) : s).trim();
    const nombre = (coma >= 0 ? s.slice(coma + 1) : '').trim();
    const parts = apellidos.split(/\s+/).filter(Boolean);
    return { nombre, apellido1: parts[0] || '', apellido2: parts.slice(1).join(' ') };
  };

  const openCrear = () => {
    // Un alta a medias de esta fila se completa, no se repite: el modal vuelve a abrir su resultado.
    if (crearResultado?.estado === 'a-medias') {
      setCrearVisible(true);
      return;
    }
    const { nombre, apellido1, apellido2 } = splitNombreCU2(selectedItem?.nombre);
    setCrearDatos({
      tratamientoId: 0,
      nombre,
      apellido1,
      apellido2,
      nif: selectedItem?.nif,
      cp: selectedItem?.cp,
      email: selectedItem?.email,
      numColegiado: selectedItem?.numColegiado,
    });
    setCrearResultado(null);
    setCrearVisible(true);
  };

  // El resultado de crear o completar: vinculado, la fila lo refleja y el card "Datos Gescom" carga el cliente creado.
  const aplicarCreacion = (result: ResultadoCreacionFarmaceutico) => {
    setCrearResultado(prev => ({ ...result, codigoCl: result.codigoCl ?? prev?.codigoCl }));
    if (!selectedItem || result.estado !== 'vinculado') return;
    setResultadoVinculacion({ estado: 'vinculado', mensaje: result.mensaje, idCliente: result.idCliente });
    const updated = { ...selectedItem, idColegiadoGS: result.idCliente };
    setSelectedItem(updated);
    setData(prev => prev.map(f => f.id === updated.id ? updated : f));
    setFilteredData(prev => prev.map(f => f.id === updated.id ? updated : f));
    loadGescomCliente(result.idCliente);
  };

  // Crear y vincular (#164). Un rechazo antes del alta llega como 400, 404 o 409 y no crea nada.
  const handleCrear = async () => {
    if (!selectedItem) return;
    setCreando(true);
    try {
      aplicarCreacion(await crearFarmaceuticoGescom(selectedItem.id, crearDatos));
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Error al crear el registro en Gescom';
      message.error(msg);
    } finally {
      setCreando(false);
    }
  };

  // Completar sobre el cliente ya creado lo que quedó a medias, sin crear otro (el alta no es idempotente).
  const handleCompletar = async () => {
    if (!selectedItem || !crearResultado) return;
    setCreando(true);
    try {
      aplicarCreacion(await completarFarmaceuticoGescom(selectedItem.id, crearResultado.idCliente,
        { nif: crearDatos.nif, numColegiado: crearDatos.numColegiado }));
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Error al completar el registro en Gescom';
      message.error(msg);
    } finally {
      setCreando(false);
    }
  };

  const handleEstadoChange = (value: string | number) => {
    setSelectedEstado(Number(value));
  };

  const columns: ColumnsType<ReconciliacionFarmaceutico> = [
    {
      title: 'Rec',
      dataIndex: 'reconciliacionId',
      key: 'rec',
      className: 'fct-num',
      align: 'right',
      width: 45,
      render: (val: number) => <Text type="secondary" style={{ fontSize: 12 }}>{val}</Text>,
    },
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
      ellipsis: true,
      width: 220,
    },
    {
      title: 'id_colegiado',
      dataIndex: 'idColegiadoCU2',
      key: 'idColegiado',
      className: 'fct-num',
      align: 'right',
      width: 100,
      render: (val: number) => <Text code>{val}</Text>,
    },
    {
      title: 'id_colegiado_gs',
      dataIndex: 'idColegiadoGS',
      key: 'idColegiadoGS',
      className: 'fct-num',
      align: 'right',
      width: 110,
      render: (val?: number) => val ? <Text code>{val}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Nº Colegiado',
      dataIndex: 'numColegiado',
      key: 'numColegiado',
      className: 'fct-mono',
      width: 110,
    },
    {
      title: 'Colegio',
      dataIndex: 'colegio',
      key: 'colegio',
      width: 140,
      ellipsis: true,
    },
    {
      title: 'NIF',
      dataIndex: 'nif',
      key: 'nif',
      className: 'fct-mono',
      width: 100,
    },
    {
      title: 'Rol',
      dataIndex: 'rol',
      key: 'rol',
      width: 90,
      filters: [
        { text: 'Titular', value: 'titular' },
        { text: 'Empleado', value: 'empleado' },
      ],
      onFilter: (value, record) => (record.rol || '').toLowerCase() === (value as string).toLowerCase(),
      // Rol con señal visual (aceptación GCVSC-I001, 2026-10-04): Titular en proceso (era azul), el resto neutro.
      render: (val?: string) => val
        ? <FctEstado personalizado={{ etiqueta: val, familia: val.toLowerCase() === 'titular' ? 'proceso' : 'neutro', icono: val.toLowerCase() === 'titular' ? 'usuario' : 'equipo' }} />
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Rel.',
      key: 'relaciones',
      width: 60,
      render: (_, r) => r.relacionesPendientes ? (
        <Tooltip title="Tiene titularidades/empleados pendientes"><span><FctGlifoAtencion nivel="atencion" /> Sí</span></Tooltip>
      ) : <Text type="secondary">—</Text>,
    },
    {
      title: 'Fecha',
      dataIndex: 'fechaCreacion',
      key: 'fecha',
      className: 'fct-num',
      align: 'right',
      width: 100,
      render: (val: string) => new Date(val).toLocaleDateString('es-ES'),
    },
    {
      title: 'Estado',
      dataIndex: 'estadoId',
      key: 'estado',
      width: 100,
      render: (val: number, record) => {
        const tag = <EstadoOperacion estadoId={val} etiqueta={estadoLabels[val]?.label || `Estado ${val}`} />;
        // En descartados, el comentario de gestión se consulta sin abrir la ficha
        return val === ESTADO_DESCARTADO && record.notasGestion
          ? <Tooltip title={record.notasGestion}>{tag}</Tooltip>
          : tag;
      },
    },
    {
      title: '',
      key: 'notas',
      width: 40,
      render: (_, r) => r.notasGestion ? (
        <Tooltip title={r.notasGestion}><FileTextOutlined /></Tooltip>
      ) : null,
    },
  ];

  if (error) {
    return (
      <Result
        status="error"
        title="Error al cargar farmacéuticos"
        extra={<Button type="primary" onClick={loadData} icon={<ReloadOutlined />}>Reintentar</Button>}
      />
    );
  }

  // ¿Ya está vinculado este item?
  const yaVinculado = selectedItem ? !!selectedItem.idColegiadoGS : false;

  // Coincidencias verificadas tras vinculación
  const matchIdColegiado = yaVinculado && clienteGescomData?.idColegiadoCU2 === selectedItem?.idColegiadoCU2;
  const matchIdGS = yaVinculado && selectedItem?.idColegiadoGS === clienteGescomData?.id;
  const greenCodeStyle: React.CSSProperties = { backgroundColor: fct.estado.exito.relleno, border: `1px solid ${fct.estado.exito.linea}`, borderRadius: fct.radio.micro, padding: '0 4px' };

  return (
    <div>
      {/* Cabecera */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Input
          placeholder="Buscar por nombre, colegiado, NIF..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={e => handleSearch(e.target.value)}
          style={{ width: 280 }}
          allowClear
        />
        <Segmented
          value={selectedEstado}
          onChange={handleEstadoChange}
          disabled={loading || loadingCounts}
          options={[
            {
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>Todos</span>
                  <Badge
                    count={estadoCounts.pendiente + estadoCounts.realizado + estadoCounts.descartado}
                    style={{ backgroundColor: fct.tinta['700'] }}
                    showZero
                    overflowCount={9999}
                  />
                </div>
              ),
              value: ESTADO_TODOS,
            },
            {
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>Pendiente</span>
                  <Badge
                    count={estadoCounts.pendiente}
                    style={{ backgroundColor: fct.estado.aviso.linea }}
                    showZero
                    overflowCount={9999}
                  />
                </div>
              ),
              value: ESTADO_PENDIENTE,
            },
            {
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>Realizado</span>
                  <Badge
                    count={estadoCounts.realizado}
                    style={{ backgroundColor: fct.estado.exito.linea }}
                    showZero
                    overflowCount={9999}
                  />
                </div>
              ),
              value: ESTADO_REALIZADO,
            },
            {
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>Descartado</span>
                  <Badge
                    count={estadoCounts.descartado}
                    style={{ backgroundColor: fct.estado.neutro.linea }}
                    showZero
                    overflowCount={9999}
                  />
                </div>
              ),
              value: ESTADO_DESCARTADO,
            },
          ]}
          size="large"
        />
        <Button icon={<ReloadOutlined />} onClick={() => { loadData(); loadCounts(); }}>Refrescar</Button>
      </div>

      {/* Tabla */}
      <Table
        dataSource={filteredData.map(f => ({ ...f, key: f.id }))}
        columns={columns}
        loading={loading}
        pagination={{ defaultPageSize: 100, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'], showTotal: total => `${total} registros` }}
        onRow={record => ({
          onClick: () => openDetail(record),
          style: { cursor: 'pointer' },
        })}
        rowClassName={(r) => claseFilaAtencion(r.relacionesPendientes ? 'atencion' : 'normal')}
        size="middle"
        scroll={{ x: 1100 }}
        locale={{ emptyText: <Empty description="No hay farmacéuticos pendientes de conciliación" /> }}
      />

      {/* Modal de detalle — gestión de vinculación */}
      <Modal
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        title={selectedItem ? `Gestión de farmacéutico — ${selectedItem.nombre || 'ID CU2 ' + selectedItem.idColegiadoCU2}` : ''}
        width={900}
        destroyOnClose
      >
        {selectedItem && (
          <div>
            {/* Relaciones pendientes — aviso superior */}
            {selectedItem.relacionesPendientes && (
              <div style={{ background: fct.estado.aviso.relleno, border: `1px solid ${fct.estado.aviso.linea}`, borderRadius: fct.radio.control, padding: 12, marginBottom: 16 }}>
                <FctGlifoAtencion nivel="atencion" />{' '}
                <Text strong style={{ color: fct.estado.aviso.texto }}>Titularidades / empleados pendientes de este farmacéutico</Text>
                <div style={{ marginTop: 4 }}>
                  <Text style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{selectedItem.relacionesPendientes}</Text>
                </div>
              </div>
            )}

            {/* Dos columnas: CU2 (izq) | Gescom (der) */}
            <Row gutter={16}>
              {/* ─── Columna CU2 ─── */}
              <Col xs={24} md={12}>
                <Card
                  title="Datos CU2"
                  size="small"
                  style={{ marginBottom: 16 }}
                >
                  <Descriptions size="small" column={1} colon={false} labelStyle={{ fontWeight: 500, width: 120, color: fct.tinta['700'] }}>
                    <Descriptions.Item label="Nombre"><Text strong>{selectedItem.nombre || '—'}</Text></Descriptions.Item>
                    <Descriptions.Item label="id_colegiado">
                      <span style={matchIdColegiado ? greenCodeStyle : undefined}><Text code>{selectedItem.idColegiadoCU2}</Text></span>
                    </Descriptions.Item>
                    <Descriptions.Item label="id_colegiado_gs">
                      {selectedItem.idColegiadoGS
                        ? <span style={matchIdGS ? greenCodeStyle : undefined}><Text code>{selectedItem.idColegiadoGS}</Text></span>
                        : <Text type="secondary">sin vincular</Text>
                      }
                    </Descriptions.Item>
                    <Descriptions.Item label="Nº Colegiado"><span className="fct-mono">{selectedItem.numColegiado || '—'}</span></Descriptions.Item>
                    <Descriptions.Item label="Colegio">{selectedItem.colegio || '—'}</Descriptions.Item>
                    <Descriptions.Item label="NIF"><span className="fct-mono">{selectedItem.nif || '—'}</span></Descriptions.Item>
                    <Descriptions.Item label="CP"><span className="fct-mono">{selectedItem.cp || '—'}</span></Descriptions.Item>
                    <Descriptions.Item label="Email">{selectedItem.email || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Rol">{selectedItem.rol || '—'}</Descriptions.Item>
                  </Descriptions>
                </Card>
                {!yaVinculado && (
                  <Space style={{ marginBottom: 16 }}>
                    <Button icon={<SearchOutlined />} onClick={openBuscar}>Buscar en Gescom</Button>
                    <Button icon={<UserAddOutlined />} onClick={openCrear}>Crear en Gescom</Button>
                  </Space>
                )}
              </Col>

              {/* ─── Columna Gescom ─── */}
              <Col xs={24} md={12}>
                <Card
                  title="Datos Gescom"
                  size="small"
                  style={{ marginBottom: 16 }}
                >
                  <Spin spinning={clienteGescomLoading}>
                    <Descriptions size="small" column={1} colon={false} labelStyle={{ fontWeight: 500, width: 120, color: fct.tinta['700'] }}>
                      <Descriptions.Item label="Nombre">
                        {/* Sin enlace a Gedeco: el ID ya es de Gescom, que Gedeco no conoce (#149, #151). */}
                        <Text strong>{clienteGescomData ? clienteGescomData.nombre : '—'}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="id_colegiado_CU2">
                        {clienteGescomData?.idColegiadoCU2
                          ? <span style={matchIdColegiado ? greenCodeStyle : undefined}><Text code>{clienteGescomData.idColegiadoCU2}</Text></span>
                          : <Text type="secondary">{clienteGescomData ? 'sin asignar' : '—'}</Text>
                        }
                      </Descriptions.Item>
                      <Descriptions.Item label="ID">
                        {yaVinculado ? (
                          <span style={matchIdGS ? greenCodeStyle : undefined}><Text code>{selectedItem.idColegiadoGS}</Text></span>
                        ) : (
                          <>
                            <Space.Compact size="small">
                              <Input
                                placeholder="ID"
                                value={clienteGescomIdInput}
                                onChange={e => setClienteGescomIdInput(e.target.value)}
                                onPressEnter={handleBuscarClienteGescom}
                                style={{ width: 100 }}
                              />
                              <Button
                                icon={<ReloadOutlined />}
                                onClick={handleBuscarClienteGescom}
                                loading={clienteGescomLoading}
                                size="small"
                              />
                            </Space.Compact>
                            {clienteGescomError && (
                              <Text type="danger" style={{ fontSize: 12, marginLeft: 8 }}>{clienteGescomError}</Text>
                            )}
                          </>
                        )}
                      </Descriptions.Item>
                      <Descriptions.Item label="Nº Colegiado"><span className="fct-mono">{clienteGescomData?.numColegiado || <Text type="secondary">—</Text>}</span></Descriptions.Item>
                      <Descriptions.Item label="Provincia">{clienteGescomData?.provincia || <Text type="secondary">—</Text>}</Descriptions.Item>
                      <Descriptions.Item label="NIF"><span className="fct-mono">{clienteGescomData?.nif || <Text type="secondary">—</Text>}</span></Descriptions.Item>
                      <Descriptions.Item label="CP"><span className="fct-mono">{clienteGescomData?.cp || <Text type="secondary">—</Text>}</span></Descriptions.Item>
                      <Descriptions.Item label="Email">{clienteGescomData?.email || <Text type="secondary">—</Text>}</Descriptions.Item>
                    </Descriptions>
                  </Spin>
                </Card>
              </Col>
            </Row>

            {/* Zona de vinculación */}
            {yaVinculado ? (
              <div style={{ background: fct.estado.exito.relleno, border: `1px solid ${fct.estado.exito.linea}`, borderRadius: fct.radio.control, padding: 12, marginBottom: 16, textAlign: 'center' }}>
                <FctEstado personalizado={{ etiqueta: 'Registros vinculados', familia: 'exito', icono: 'check' }} />
              </div>
            ) : clienteGescomData && selectedItem.estadoId === ESTADO_PENDIENTE
              && (!clienteGescomData.idColegiadoCU2 || clienteGescomData.idColegiadoCU2 === selectedItem.idColegiadoCU2) ? (
              <div style={{ background: fct.estado.exito.relleno, border: `1px solid ${fct.estado.exito.linea}`, borderRadius: fct.radio.control, padding: 16, marginBottom: 16, textAlign: 'center' }}>
                {clienteGescomData.idColegiadoCU2 === selectedItem.idColegiadoCU2 && (
                  <div style={{ marginBottom: 8 }}>
                    <FctGlifoAtencion nivel="atencion" />{' '}
                    <Text type="warning">
                      Vinculación incompleta: el registro Gescom ya apunta a este colegiado CU2, pero falta el id_colegiado_gs en CU2.
                    </Text>
                  </div>
                )}
                <Button
                  type="primary"
                  size="large"
                  icon={<LinkOutlined />}
                  loading={vinculando}
                  onClick={() => {
                    Modal.confirm({
                      width: 520,
                      title: 'Vincular registros',
                      content: (
                        <div>
                          <p>Se vinculará el farmacéutico de CU2 con el registro de Gescom:</p>
                          <ul style={{ margin: '8px 0' }}>
                            <li><strong>CU2:</strong> {selectedItem.nombre} (id_colegiado: {selectedItem.idColegiadoCU2})</li>
                            <li><strong>Gescom:</strong> {clienteGescomData.nombre} (ID: {clienteGescomData.id})</li>
                          </ul>
                          <p>La vinculación se registrará en ambas bases de datos. ¿Continuar?</p>
                        </div>
                      ),
                      okText: 'Vincular',
                      cancelText: 'Cancelar',
                      onOk: handleVincular,
                    });
                  }}
                >
                  Vincular registros
                </Button>
              </div>
            ) : null}

            {/* Cómo quedó la vinculación: en los dos lados, o a medias, con su reintento (#178). */}
            {resultadoVinculacion && (
              <div style={{ marginBottom: 16 }}>
                <FctAviso
                  tipo={resultadoVinculacion.estado === 'vinculado' ? 'exito' : 'error'}
                  titulo={resultadoVinculacion.mensaje}
                >
                  {resultadoVinculacion.estado === 'a-medias' && (
                    <Button size="small" icon={<LinkOutlined />} loading={vinculando} onClick={handleVincular} style={{ marginTop: 8 }}>
                      Volver a vincular
                    </Button>
                  )}
                </FctAviso>
              </div>
            )}

            <Divider style={{ margin: '12px 0' }} />

            {/* Comentario */}
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Comentario</Text>
            <TextArea
              value={editNotas}
              onChange={e => setEditNotas(e.target.value)}
              autoSize={{ minRows: 2, maxRows: 5 }}
              placeholder="Añadir comentario..."
              style={{ marginBottom: 16 }}
            />

            {/* Acciones: estado selector + Guardar */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <Select
                value={editEstadoId}
                onChange={setEditEstadoId}
                style={{ width: 160 }}
              >
                <Select.Option value={ESTADO_PENDIENTE}><FctEstado personalizado={{ etiqueta: 'Pendiente', familia: 'aviso', icono: 'hora' }} /></Select.Option>
                <Select.Option value={ESTADO_REALIZADO}><FctEstado personalizado={{ etiqueta: 'Realizado', familia: 'exito' }} /></Select.Option>
                <Select.Option value={ESTADO_DESCARTADO}><FctEstado personalizado={{ etiqueta: 'Descartado', familia: 'neutro', icono: 'cancelado' }} /></Select.Option>
              </Select>

              <Button
                icon={<SaveOutlined />}
                onClick={() => handleUpdateEstado(selectedItem.id, editEstadoId, editNotas || undefined)}
                loading={actionLoading}
              >
                Guardar
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Sub-modal: Buscar en Gescom (GDO-T38) */}
      <Modal
        open={buscarVisible}
        onCancel={() => setBuscarVisible(false)}
        footer={null}
        title="Buscar farmacéutico en Gescom"
        width={860}
        destroyOnClose
      >
        <Space.Compact style={{ width: '100%', marginBottom: 12 }}>
          <Input
            value={buscarTexto}
            onChange={e => setBuscarTexto(e.target.value)}
            onPressEnter={handleBuscarCandidatos}
            placeholder="Apellidos, Nombre"
            allowClear
            autoFocus
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            loading={buscandoCandidatos}
            onClick={handleBuscarCandidatos}
          >
            Buscar
          </Button>
        </Space.Compact>
        <Table
          size="small"
          dataSource={candidatos.map(c => ({ ...c, key: c.id }))}
          loading={buscandoCandidatos}
          pagination={false}
          scroll={{ y: 360 }}
          onRow={c => ({
            onClick: () => handleSeleccionCandidato(c),
            style: { cursor: 'pointer' },
          })}
          locale={{
            emptyText: (
              <Empty description={busquedaHecha
                ? 'Sin candidatos para ese texto'
                : 'Edita el texto si lo necesitas y pulsa Buscar'} />
            ),
          }}
          columns={[
            {
              title: 'Simil.',
              key: 'score',
              width: 70,
              render: (_, c: CandidatoGescom) => (
                <FctEstado personalizado={{ etiqueta: `${c.score}%`, familia: c.confianza === 'SEGURO' ? 'exito' : c.confianza === 'DUDOSO' ? 'aviso' : 'neutro' }} />
              ),
            },
            {
              title: 'Cliente',
              dataIndex: 'nombre',
              key: 'nombre',
              ellipsis: true,
              // Los candidatos son de Gescom (#162): sin enlace a Gedeco, que no conoce sus IDs.
              render: (val: string) => <Text>{val}</Text>,
            },
            {
              title: 'ID',
              dataIndex: 'id',
              key: 'id',
              className: 'fct-num',
              align: 'right',
              width: 90,
              render: (val: number) => <Text code>{val}</Text>,
            },
            { title: 'Código', dataIndex: 'codigo', key: 'codigo', className: 'fct-mono', width: 100 },
            { title: 'Nº Colegiado', dataIndex: 'numColegiado', key: 'numColegiado', className: 'fct-mono', width: 110 },
            { title: 'Provincia', dataIndex: 'provincia', key: 'provincia', width: 120, ellipsis: true },
            { title: 'NIF', dataIndex: 'nif', key: 'nif', className: 'fct-mono', width: 90 },
            {
              title: 'Estado',
              key: 'estado',
              width: 130,
              render: (_, c: CandidatoGescom) => (
                <Space size={4} wrap>
                  {c.idColegiadoCU2 && (
                    <Tooltip title={`Ya vinculado al colegiado CU2 ${c.idColegiadoCU2} — posible duplicado`}>
                      <span><FctGlifoAtencion nivel="urgente" /> <span className="fct-codigo">CU2 {c.idColegiadoCU2}</span></span>
                    </Tooltip>
                  )}
                  {c.fechaBaja && (
                    <Tooltip title={`Dado de baja en Gescom el ${new Date(c.fechaBaja).toLocaleDateString('es-ES')}`}>
                      <Tag style={{ marginInlineEnd: 0 }}>Baja {new Date(c.fechaBaja).toLocaleDateString('es-ES')}</Tag>
                    </Tooltip>
                  )}
                  {!c.idColegiadoCU2 && !c.fechaBaja && <Text type="secondary">—</Text>}
                </Space>
              ),
            },
          ]}
        />
        <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
          Haz click en un candidato para cargarlo en el card "Datos Gescom" del caso.
        </Text>
      </Modal>

      {/* Sub-modal: Crear en Gescom y vincular en un paso (#164) */}
      <Modal
        open={crearVisible}
        onCancel={() => setCrearVisible(false)}
        footer={null}
        title="Crear farmacéutico en Gescom"
        width={520}
        destroyOnClose
      >
        {!crearResultado ? (
          <div>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              Verifica los datos con los que se creará el registro en Gescom. Quedará vinculado a este colegiado:
            </Text>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <Text style={{ width: 110, fontWeight: 500, color: fct.tinta['700'] }}>id_colegiado</Text>
              <Text code>{selectedItem?.idColegiadoCU2}</Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <Text style={{ width: 110, fontWeight: 500, color: fct.tinta['700'] }}>Tratamiento *</Text>
              <Select
                value={crearDatos.tratamientoId || undefined}
                onChange={value => setCrearDatos(prev => ({ ...prev, tratamientoId: value }))}
                placeholder="Selecciona..."
                style={{ width: 120 }}
                options={[
                  { value: 1, label: 'Sr.' },
                  { value: 2, label: 'Sra.' },
                ]}
              />
            </div>
            {([
              ['Nombre *', 'nombre'],
              ['Apellido 1 *', 'apellido1'],
              ['Apellido 2', 'apellido2'],
              ['NIF', 'nif'],
              ['CP', 'cp'],
              ['Email', 'email'],
              ['Nº Colegiado', 'numColegiado'],
            ] as [string, keyof CrearFarmaceuticoGescomRequest][]).map(([label, campo]) => (
              <div key={campo} style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <Text style={{ width: 110, fontWeight: 500, color: fct.tinta['700'] }}>{label}</Text>
                <Input
                  value={crearDatos[campo] || ''}
                  onChange={e => setCrearDatos(prev => ({ ...prev, [campo]: e.target.value }))}
                  style={{ flex: 1 }}
                />
              </div>
            ))}
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Button
                type="primary"
                icon={<UserAddOutlined />}
                loading={creando}
                disabled={!crearDatos.nombre.trim() || !crearDatos.apellido1.trim() || !crearDatos.tratamientoId}
                onClick={() => {
                  Modal.confirm({
                    title: 'Crear registro en Gescom',
                    content: `Se creará el cliente "${crearDatos.tratamientoId === 2 ? 'Sra.' : 'Sr.'} ${crearDatos.apellido1} ${crearDatos.apellido2 || ''}, ${crearDatos.nombre}" en Gescom y se vinculará a este colegiado. ¿Continuar?`,
                    okText: 'Crear',
                    cancelText: 'Cancelar',
                    onOk: handleCrear,
                  });
                }}
              >
                Crear registro
              </Button>
            </div>
          </div>
        ) : (
          <Result
            status={crearResultado.estado === 'vinculado' ? 'success' : 'warning'}
            title={crearResultado.estado === 'vinculado' ? 'Registro creado y vinculado' : 'Registro creado a medias'}
            subTitle={
              <>
                <div>{crearResultado.mensaje}</div>
                <div>
                  Cliente de Gescom:{' '}
                  <Text code copyable style={{ fontSize: 16 }}>{crearResultado.codigoCl || crearResultado.idCliente}</Text>
                </div>
              </>
            }
            extra={[
              ...(crearResultado.estado === 'a-medias' ? [
                <Button key="completar" type="primary" icon={<LinkOutlined />} loading={creando} onClick={handleCompletar}>
                  Completar la vinculación
                </Button>,
              ] : []),
              <Button key="cerrar" onClick={() => setCrearVisible(false)}>
                Cerrar
              </Button>,
            ]}
          />
        )}
      </Modal>
    </div>
  );
};

export default FarmaceuticosTab;
